import { cx, useTranslations } from "@jugl-web/utils";
import React, { ReactNode, useState } from "react";
import { Backdrop, PlainButton, Tooltip } from "@jugl-web/ui-components";
import { Portal } from "@headlessui/react";
import { Button } from "@jugl-web/ui-kit";
import { useChatBot } from "@jugl-web/domain-resources/chat-bot";
import ghostImage from "./assets/ghost.png";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";
import { ReactComponent as QuestionIcon } from "./assets/question.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { HelpItemAccordion } from "./components/HelpItemAccordion";

export const TrialHelpWidget: React.FC = () => {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const { isInitialized: isChatBotInitialized, show: showChatBot } =
    useChatBot();

  const highlightedText = (text: ReactNode) => (
    <span className="font-medium text-white">{text}</span>
  );

  return (
    <Portal>
      {isOpen && <Backdrop />}
      <Tooltip
        placement="top"
        isDisabled={isOpen}
        renderTrigger={({ props, ref }) => (
          <PlainButton
            className={cx(
              "fixed right-10 bottom-10 z-50 flex h-12 w-12 items-center justify-center rounded-full border border-solid border-white bg-grey-100 shadow-[0_6px_8px_0_rgba(0,0,0,0.10)] hover:bg-grey-200",
              isOpen && "bg-grey-200"
            )}
            onClick={() => setIsOpen((prev) => !prev)}
            ref={ref}
            {...props}
          >
            <QuestionIcon />
          </PlainButton>
        )}
      >
        {t({
          id: "cx-trial-page-page.need-help-check-this-out",
          defaultMessage: "Need Help? Check this out 👇",
        })}
      </Tooltip>
      <div
        className={cx(
          "pointer-events-none fixed right-9 bottom-[100px] z-50 flex h-[800px] max-h-[calc(100%-140px)] w-[430px] max-w-[calc(100%-40px)] flex-col rounded-[20px] bg-dark opacity-0 transition-opacity",
          isOpen && "pointer-events-auto opacity-100"
        )}
      >
        <ArrowIcon className="absolute right-5 bottom-0 translate-y-full" />
        <PlainButton
          className="absolute top-4 right-4"
          onClick={() => setIsOpen(false)}
        >
          <CrossIcon />
        </PlainButton>
        <div className="flex items-center gap-[21px] border-0 border-b border-solid border-dark-800 pt-6">
          <img
            src={ghostImage}
            alt=""
            className="pointer-events-none relative h-[60px] translate-y-[0.5px]"
          />
          <span className="font-secondary font-semibold text-white">
            {t({
              id: "cx-trial-page-page.everything-you-need-to-know",
              defaultMessage: "Everything you need to know",
            })}
          </span>
        </div>
        <div className="jugl__custom-scrollbar flex h-full flex-col gap-6 overflow-y-auto p-6 pt-4">
          <HelpItemAccordion
            title={t({
              id: "cx-trial-page-page.available-channels",
              defaultMessage: "Available Channels",
            })}
          >
            <span>
              {t({
                id: "cx-trial-page-page.jugl-allow-to-connect-next-channels",
                defaultMessage: "Jugl allow to connect next Channels:",
              })}
            </span>
            <span className="mt-2.5">
              {t({
                id: "cx-trial-page-pag.whatsapp",
                defaultMessage: "🟢 WhatsApp",
              })}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.instagram",
                defaultMessage: "🟣 Instagram",
              })}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.web-chat",
                defaultMessage: "🔵 Web Chat widget",
              })}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.sms-chat",
                defaultMessage: "⚪ Text (SMS) Chat",
              })}
            </span>
            <span className="mt-5">
              {t(
                {
                  id: "cx-trial-page-page.assign-conversations-to-specific-chat-managers",
                  defaultMessage:
                    "Assign conversations to specific chat managers or teams, use AI Agent to <highlightedText>save 40% time</highlightedText> of human resource",
                },
                {
                  highlightedText,
                }
              )}
            </span>
          </HelpItemAccordion>
          <HelpItemAccordion
            title={t({
              id: "cx-trial-page-page.ai-agent",
              defaultMessage: "AI Agent",
            })}
            className="gap-5"
          >
            <span>
              {t(
                {
                  id: "cx-trial-page-page.description-personal-assistant",
                  defaultMessage:
                    "🔮 AI Agent - your <highlightedText>personal assistant</highlightedText>, trained based on information about your business",
                },
                {
                  highlightedText,
                }
              )}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.accuracy-training-sources",
                defaultMessage:
                  "Accuracy of AI Agent responses will depend on how detailed training sources will be. So make sure to provide enough information for Agent to be able to cover any question 🙌",
              })}
            </span>
            <span>
              {t(
                {
                  id: "cx-trial-page-page.message-credit-info",
                  defaultMessage:
                    "ℹ️ AI Agent will use 1 <highlightedText>Message Credit</highlightedText> every time when its sending message response to your Customers. Each plan, including Free, have its own amount of given Message Credits per month",
                },
                {
                  highlightedText,
                }
              )}
            </span>
          </HelpItemAccordion>
          <HelpItemAccordion
            title={t({
              id: "cx-trial-page-page.training-sources",
              defaultMessage: "Training Sources",
            })}
            className="gap-5"
          >
            <span>
              {t(
                {
                  id: "cx-trial-page-page.training-sources-main",
                  defaultMessage:
                    "📚 Training Sources - information about your <highlightedText>business/work</highlightedText>, based on which AI Agent will be trained",
                },
                {
                  highlightedText,
                }
              )}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.instructions-purpose",
                defaultMessage:
                  "🚀 Instructions - simple guidelines that tell the AI what is its role, how it should behave, what should do, and how it should respond to Customers",
              })}
            </span>
            <span>
              {t(
                {
                  id: "cx-trial-page-page.website-main-source",
                  defaultMessage:
                    "🌎 <highlightedText>Website - main source of training.</highlightedText>{br}A URL’s to online pages the AI can learn information from.",
                },
                {
                  br: <br />,
                  highlightedText,
                }
              )}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.files-reference-material",
                defaultMessage:
                  "📂 Files - uploaded documents (PDFs, DOCX, or TXT) the AI uses as reference material",
              })}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.text-snippets-purpose",
                defaultMessage:
                  "📙 Text snippets - short pieces of written content that teach the AI specific information",
              })}
            </span>
            <span>
              {t({
                id: "cx-trial-page-page.qa-guide-answers",
                defaultMessage:
                  "📝 Q&A - prepared questions and answers that guide how the AI should respond in common situations",
              })}
            </span>
          </HelpItemAccordion>
          <HelpItemAccordion
            title={t({
              id: "cx-trial-page-page.agent-appearance",
              defaultMessage: "Agent Appearance",
            })}
            className="gap-5"
          >
            <span>
              {t({
                id: "cx-trial-page-page.agent-appearance-personalization",
                defaultMessage:
                  "🎨 You can personalise AI Agent appearance, defining on how it will be visible for your Customers in conversations",
              })}
            </span>
            <span>
              {t(
                {
                  id: "cx-trial-page-page.picture-upload-guidelines",
                  defaultMessage:
                    "<highlightedText>Agent picture</highlightedText> - upload JPG, PNG, and SVG image up to 1 MB. You can use your Business Logo. Avoid placing real human photo, as it might create misleading perception of AI content",
                },
                {
                  highlightedText,
                }
              )}
            </span>
            <div className="flex flex-col gap-2.5">
              <span>
                {t(
                  {
                    id: "cx-trial-page-page.agent-name-examples",
                    defaultMessage:
                      "<highlightedText>Agent name</highlightedText>- use short form of a name.{br}✍️ Here is a couple of name examples you can use:",
                  },
                  {
                    br: <br />,
                    highlightedText,
                  }
                )}
              </span>
              <ul className="m-0 pl-5">
                <li>
                  {t({
                    id: "cx-trial-page-page.business-name-bot",
                    defaultMessage: "'<Your Business Name>' + Bot",
                  })}
                </li>
                <li>
                  {t({
                    id: "cx-trial-page-page.smart-support",
                    defaultMessage: "Smart Support",
                  })}
                </li>
                <li>
                  {t({
                    id: "cx-trial-page-page.ai-helper",
                    defaultMessage: "AI Helper",
                  })}
                </li>
                <li>
                  {t({
                    id: "cx-trial-page-page.ai-assistant",
                    defaultMessage: "AI Assistant",
                  })}
                </li>
                <li>
                  {t({
                    id: "cx-trial-page-page.brain",
                    defaultMessage: "Brain",
                  })}
                </li>
                <li>
                  {t({
                    id: "cx-trial-page-page.smarty",
                    defaultMessage: "Smarty",
                  })}
                </li>
              </ul>
            </div>
            <span>
              {t({
                id: "cx-trial-page-page.configurable-per-channel",
                defaultMessage:
                  "After creation of AI Agent - you will be able to configure appearance of each Channel separately, with many additional settings ⚙️",
              })}
            </span>
          </HelpItemAccordion>
        </div>
        {isChatBotInitialized && (
          <Button
            className="m-6 shrink-0"
            iconEnd={<ChevronIcon />}
            onClick={() => {
              showChatBot();
              setIsOpen(false);
            }}
          >
            {t({
              id: "cx-trial-page-page.support-chat",
              defaultMessage: "Support chat",
            })}
          </Button>
        )}
      </div>
    </Portal>
  );
};
